// frontend/src/components/PixCobrancaSetupModal/PixCobrancaSetupModal.jsx
import React, { useEffect, useState } from 'react';
import { Modal, Drawer, Form, Input, Alert, App, Button } from 'antd';
import { QrcodeOutlined, BankOutlined } from '@ant-design/icons';
import { updatePerfil } from '../../services/userService';
import { useAuth } from '../../contexts/AuthContext';
import { getErrorMessage } from '../../utils/errorHandler';

/**
 * Modal rápido para cadastrar Pix/banco ao marcar uma cobrança,
 * sem precisar ir até Configurações.
 */
export function needsPixCobrancaSetup(user) {
    const pix = user?.chave_pix_padrao ? String(user.chave_pix_padrao).trim() : '';
    const banco = user?.banco_preferencia ? String(user.banco_preferencia).trim() : '';
    return !pix || !banco;
}

export default function PixCobrancaSetupModal({ open, onClose, onSaved, isMobile = false }) {
    const { message } = App.useApp();
    const { user, refreshUser } = useAuth();
    const [form] = Form.useForm();
    const [saving, setSaving] = useState(false);

    useEffect(() => {
        if (open) {
            form.setFieldsValue({
                chave_pix_padrao: user?.chave_pix_padrao || '',
                banco_preferencia: user?.banco_preferencia || '',
            });
        }
    }, [open, user, form]);

    const handleOk = async () => {
        try {
            const values = await form.validateFields();
            setSaving(true);
            await updatePerfil({
                chave_pix_padrao: values.chave_pix_padrao.trim(),
                banco_preferencia: values.banco_preferencia.trim(),
            });
            if (typeof refreshUser === 'function') {
                await refreshUser();
            }
            message.success('Pix e banco salvos para suas cobranças.');
            onSaved?.();
            onClose();
        } catch (err) {
            if (err?.errorFields) return;
            message.error(getErrorMessage(err, 'Não foi possível salvar o Pix.'));
        } finally {
            setSaving(false);
        }
    };

    const formBody = (
        <>
            <Alert
                type="info"
                showIcon
                style={{ marginBottom: 16 }}
                message="Esses dados entram na mensagem do WhatsApp e no card público da cobrança."
            />
            <Form form={form} layout="vertical" requiredMark={false} size={isMobile ? 'large' : 'middle'}>
                <Form.Item
                    name="chave_pix_padrao"
                    label="Chave Pix"
                    rules={[{ required: true, message: 'Informe sua chave Pix.' }]}
                    tooltip="CPF, e-mail, telefone ou aleatória."
                >
                    <Input prefix={<QrcodeOutlined />} placeholder="Sua chave Pix" />
                </Form.Item>
                <Form.Item
                    name="banco_preferencia"
                    label="Nome do banco"
                    rules={[{ required: true, message: 'Informe o nome do banco.' }]}
                    tooltip="Ex: Nubank, Inter"
                >
                    <Input prefix={<BankOutlined />} placeholder="Ex: Nubank, Inter" />
                </Form.Item>
            </Form>
        </>
    );

    if (isMobile) {
        return (
            <Drawer
                open={open}
                onClose={onClose}
                placement="bottom"
                height="auto"
                title="Dados para cobrança WhatsApp"
                zIndex={1300}
                destroyOnClose
                styles={{
                    body: {
                        paddingBottom: 'calc(16px + env(safe-area-inset-bottom, 0px))',
                        maxHeight: '85dvh',
                        overflowY: 'auto',
                    },
                    content: { borderRadius: '16px 16px 0 0' },
                }}
                footer={
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                        <Button type="primary" size="large" block loading={saving} onClick={handleOk}>
                            Salvar
                        </Button>
                        <Button size="large" block onClick={onClose} disabled={saving}>
                            Agora não
                        </Button>
                    </div>
                }
            >
                {formBody}
            </Drawer>
        );
    }

    return (
        <Modal
            open={open}
            title="Dados para cobrança WhatsApp"
            onCancel={onClose}
            onOk={handleOk}
            okText="Salvar"
            cancelText="Agora não"
            confirmLoading={saving}
            centered
            destroyOnClose
            width={420}
            zIndex={1100}
        >
            {formBody}
        </Modal>
    );
}
